import React, { useState, FC, Children, CSSProperties, useRef, useCallback } from 'react';
import SwiperType from 'swiper';
import { Navigation, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import 'swiper/css'; // eslint-disable-line import/no-unresolved
import 'swiper/css/navigation'; // eslint-disable-line import/no-unresolved
import 'swiper/css/pagination'; // eslint-disable-line import/no-unresolved
import 'swiper/css/scrollbar'; // eslint-disable-line import/no-unresolved
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import SliderNavigation, { SliderNavigationProps } from './slider-navigation';
import Modal from 'components/commercetools-ui/organisms/modal';
import Image, { ImageProps } from '../image';

export type SliderProps = SliderNavigationProps & {
  allowArrowsOnTouchDevice?: boolean;
  className?: string;
  containerClassName?: string;
  spaceBetween?: number;
  slidesPerView?: number;
  dots?: boolean;
  fitToSlides?: boolean;
  slideWidth?: number;
  withThumbs?: boolean;
  slideWidthIsFlexible?: boolean;
} & SwiperProps;

const Slider: FC<SliderProps> = ({
  className = '',
  containerClassName = '',
  slideWidth = 300,
  slidesPerView,
  fitToSlides = false,
  arrows = false,
  innerArrows = false,
  solidArrows = false,
  dots = true,
  spaceBetween = 20,
  withThumbs = false,
  children,
  onSwiper,
  onInit,
  allowTouchMove,
  prevButtonStyles = {},
  nextButtonStyles = {},
  prevArrowStyles = {},
  nextArrowStyles = {},
  compactNavigation,
  slideWidthIsFlexible,
  allowArrowsOnTouchDevice = false,
  ...props
}) => {
  const { isTouchDevice } = useTouchDevice();

  const [init, setInit] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeImageUrl, setActiveImageUrl] = useState('');
  const [activeImageMedia, setActiveImageMedia] = useState<ImageProps['media']>();

  const handleInit = useCallback(
    (swiper: SwiperType) => {
      setInit(true);
      onInit?.(swiper);
    },
    [onInit],
  );

  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);

  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType>();

  const swiperRef = useRef<SwiperType>();

  const validToFit: boolean = !!fitToSlides && !!slideWidth && !!slidesPerView;
  const sliderWidth: CSSProperties['width'] =
    validToFit && slidesPerView ? `${spaceBetween * (slidesPerView - 1) + slideWidth * slidesPerView}px` : '100%';

  const containerClassNames = useClassNames([
    containerClassName,
    'slider_container relative',
    !init ? 'invisible' : 'visible',
    {
      'slider__container--fit': validToFit,
      'slider__container--with-thumbs': withThumbs,
    },
  ]);
  const slidesClassName = useClassNames(['slider', className]);

  const slideProps = {
    width: slideWidthIsFlexible ? 'fit-content' : `${slideWidth}px`,
  } as React.CSSProperties;

  const handleSlideClick = (child: React.ReactElement) => {
    const imageUrl =
      child.props?.src ||
      child.props?.children?.props?.src ||
      (typeof child.props?.children === 'object' && child.props.children.props?.src);

    // For handling Cloudinary images
    const mediaObject =
      child.props?.media ||
      child.props?.children?.props?.media ||
      (typeof child.props?.children === 'object' && child.props.children.props?.media);

    if (imageUrl) {
      setActiveImageUrl(imageUrl);
      setActiveImageMedia(mediaObject);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const slides = Children.map(children, (child) => (
    <SwiperSlide style={slideProps}>
      <div onClick={() => handleSlideClick(child as React.ReactElement)} className="cursor-pointer">
        {child}
      </div>
    </SwiperSlide>
  ));

  const handleOnSwiper = (swiper: SwiperType) => {
    swiperRef.current = swiper;
    onSwiper?.(swiper);

    if (withThumbs) {
      setThumbsSwiper(swiper);
    }
  };

  const modalStyle = {
    content: {
      padding: 0,
      overflow: 'hidden',
      width: '90vw',
      height: '90vh',
      backgroundColor: 'transparent',
      border: 'none',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
  };

  return (
    <>
      <div className={containerClassNames}>
        <Swiper
          key={`${allowTouchMove} ${isTouchDevice}`}
          className={slidesClassName}
          modules={[Navigation, Pagination, Thumbs]}
          thumbs={{ swiper: thumbsSwiper }}
          pagination={dots ? { clickable: true, bulletActiveClass: 'slider__bullet--active' } : false}
          slidesPerView={slidesPerView ?? 'auto'}
          spaceBetween={spaceBetween}
          style={{ width: sliderWidth }}
          navigation={{
            prevEl: navigationPrevRef.current,
            nextEl: navigationNextRef.current,
          }}
          onSwiper={handleOnSwiper}
          observer
          observeParents
          onInit={handleInit}
          allowTouchMove={allowTouchMove && isTouchDevice}
          {...props}
        >
          {slides}
        </Swiper>
        <SliderNavigation
          compactNavigation={compactNavigation}
          arrows={allowArrowsOnTouchDevice ? arrows : arrows && !isTouchDevice}
          prevButtonStyles={prevButtonStyles}
          nextButtonStyles={nextButtonStyles}
          prevArrowStyles={prevArrowStyles}
          nextArrowStyles={nextArrowStyles}
          navigationPrevRef={navigationPrevRef}
          navigationNextRef={navigationNextRef}
          totalSlides={slides?.length}
          swiperRef={swiperRef?.current}
          innerArrows={innerArrows}
          solidArrows={solidArrows}
        />
      </div>

      <Modal
        isOpen={modalOpen}
        onRequestClose={handleCloseModal}
        style={modalStyle}
        closeButton
        preventScroll
        shouldCloseOnOverlayClick={true}
        className={'text-white focus:outline-none lg:text-black'}
      >
        <div
          className="relative flex h-full w-full items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseModal();
            }
          }}
        >
          <Image
            src={activeImageUrl}
            alt="Enlarged view"
            media={activeImageMedia ? { mediaId: activeImageMedia.mediaId } : undefined}
            loading={'lazy'}
            style={{ objectFit: 'contain' }}
            className="max-h-full max-w-full object-contain"
            fill
          />
        </div>
      </Modal>
    </>
  );
};

export default Slider;
